// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import {
  BrandSearchDesktopSubtitle,
  BrandSearchMobileSubtitle,
} from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/BrandSearchSubtitle";
import { IBrandSearchQueryUXMessaging } from "@faire/web-api--source/indigofair/data/IBrandSearchQueryUXMessaging";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";

export const LOGGING_DATA_FIELD_SEPARATOR = "|";
export const LOGGING_DATA_VALUE_SEPARATOR = ":";

export const LOGGING_DATA_AVG_BRAND_REVIEW_RATING_FIELD_NAME =
  "avg_brand_review_rating";
export const LOGGING_DATA_MINIMUM_ORDER_AMOUNT_CENTS_FIELD_NAME =
  "minimum_order_amount_cents";
export const LOGGING_DATA_MINIMUM_ORDER_AMOUNT_CURRENCY_FIELD_NAME =
  "minimum_order_amount_currency";
export const LOGGING_DATA_MINIMUM_ORDER_AMOUNT_ORIGINAL_CENTS_FIELD_NAME =
  "minimum_order_amount_original_cents";
export const LOGGING_DATA_MINIMUM_ORDER_AMOUNT_ORIGINAL_CURRENCY_FIELD_NAME =
  "minimum_order_amount_original_currency";
export const LOGGING_DATA_BASED_IN_COUNTRY_DESKTOP_MESSAGE_FIELD_NAME =
  "based_in_country_desktop_message";
export const LOGGING_DATA_BASED_IN_COUNTRY_MOBILE_MESSAGE_FIELD_NAME =
  "based_in_country_mobile_message";
export const LOGGING_DATA_INSIDER_DESKTOP_MESSAGE_FIELD_NAME =
  "insider_desktop_message";
export const LOGGING_DATA_INSIDER_MOBILE_MESSAGE_FIELD_NAME =
  "insider_mobile_message";

export const brandHeaderMobileSubTitle = (loggingFields: string[]) => {
  const { brand, insiderMessage } =
    buildBrandHeaderSubtitleProps(loggingFields);
  return (
    <BrandSearchMobileSubtitle
      isLoading={false}
      brand={brand}
      insiderMessage={insiderMessage}
    />
  );
};

export const brandHeaderDesktopSubTitle = (loggingFields: string[]) => {
  const { brand, insiderMessage } =
    buildBrandHeaderSubtitleProps(loggingFields);
  return (
    <BrandSearchDesktopSubtitle
      isLoading={false}
      brand={brand}
      insiderMessage={insiderMessage}
    />
  );
};

const buildBrandHeaderSubtitleProps = (loggingFields: string[]) => {
  const parsedLoggingData = parseLoggingData(loggingFields);
  const brand = buildBrand(parsedLoggingData);
  const insiderMessage = parsedLoggingData.insider_message;
  return { brand, insiderMessage };
};

interface IParasedLoggingData {
  avg_brand_review_rating: string;
  minimum_order_amount: IFaireMoney | undefined;
  based_in_country_message: IBrandSearchQueryUXMessaging | undefined;
  insider_message: IBrandSearchQueryUXMessaging | undefined;
}

const parseLoggingData = (loggingFields: string[]): IParasedLoggingData => {
  if (!loggingFields) {
    return {
      avg_brand_review_rating: "",
      minimum_order_amount: undefined,
      based_in_country_message: undefined,
      insider_message: undefined,
    };
  }
  const getStringValue = (encodedFields: string[], fieldName: string) => {
    const fieldParts = encodedFields
      .find((field) => field.startsWith(fieldName))
      ?.split(LOGGING_DATA_VALUE_SEPARATOR);
    if (
      fieldParts &&
      fieldParts.length === 2 &&
      fieldParts[0] === fieldName &&
      typeof fieldParts[1] === "string" &&
      fieldParts[1] !== "undefined"
    ) {
      return fieldParts[1];
    }
    return "";
  };

  const getNumberValue = (encodedFields: string[], fieldName: string) => {
    const fieldParts = encodedFields
      .find((field) => field.startsWith(fieldName))
      ?.split(LOGGING_DATA_VALUE_SEPARATOR);
    if (
      fieldParts &&
      fieldParts.length === 2 &&
      fieldParts[0] === fieldName &&
      fieldParts[1] &&
      !isNaN(parseInt(fieldParts[1]))
    ) {
      return parseInt(fieldParts[1]);
    }
    return 0;
  };
  return {
    avg_brand_review_rating: getStringValue(
      loggingFields,
      LOGGING_DATA_AVG_BRAND_REVIEW_RATING_FIELD_NAME
    ),
    minimum_order_amount: IFaireMoney.build({
      amount_cents: getNumberValue(
        loggingFields,
        LOGGING_DATA_MINIMUM_ORDER_AMOUNT_CENTS_FIELD_NAME
      ),
      currency: getStringValue(
        loggingFields,
        LOGGING_DATA_MINIMUM_ORDER_AMOUNT_CURRENCY_FIELD_NAME
      ),
      original_amount_cents: getNumberValue(
        loggingFields,
        LOGGING_DATA_MINIMUM_ORDER_AMOUNT_ORIGINAL_CENTS_FIELD_NAME
      ),
      original_currency: getStringValue(
        loggingFields,
        LOGGING_DATA_MINIMUM_ORDER_AMOUNT_ORIGINAL_CURRENCY_FIELD_NAME
      ),
    }),
    based_in_country_message: IBrandSearchQueryUXMessaging.build({
      desktop_message: getStringValue(
        loggingFields,
        LOGGING_DATA_BASED_IN_COUNTRY_DESKTOP_MESSAGE_FIELD_NAME
      ),
      mobile_message: getStringValue(
        loggingFields,
        LOGGING_DATA_BASED_IN_COUNTRY_MOBILE_MESSAGE_FIELD_NAME
      ),
    }),
    insider_message: IBrandSearchQueryUXMessaging.build({
      desktop_message: getStringValue(
        loggingFields,
        LOGGING_DATA_INSIDER_DESKTOP_MESSAGE_FIELD_NAME
      ),
      mobile_message: getStringValue(
        loggingFields,
        LOGGING_DATA_INSIDER_MOBILE_MESSAGE_FIELD_NAME
      ),
    }),
  };
};

const buildBrand = (parsedLoggingData: IParasedLoggingData) => {
  return {
    avg_brand_review_rating: parsedLoggingData.avg_brand_review_rating,
    minimum_order_amount: parsedLoggingData.minimum_order_amount,
    based_in_country_message: parsedLoggingData.based_in_country_message,
  };
};
